import React, { useCallback, useEffect, useState } from "react";
import PageTitle from "../../components/Ui/PageTitle";
import { Col, Row } from "react-bootstrap";
import { TApiProvider } from "../../api/endpoints/Provider";
import { SelectSingleInput } from "../../components/Inputs/SelectInput";
import * as Api from '../../api';
import { OrderCategory, OrderStatus, TApiOrder } from "../../api/endpoints/Order";
import PaginatedOrderList from "../../components/PaginatedOrderList";
import LoadingIndicator from "../../components/Feedback/LoadingIndicator";
import TextInput from "../../components/Inputs/TextInput";

const SELECTABLE_ORDER_STATUSES = [
  OrderStatus.DRAFT,
  OrderStatus.SUBMITTED,
  OrderStatus.ACCEPTED,
  OrderStatus.REJECTED,
  OrderStatus.CANCELLED
]

const SELECTABLE_ORDER_CATEGORIES = Object.values(OrderCategory);


const CcpOrders = () => {
  const [limit, setLimit] = useState<number>(10);
  const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [providers, setProviders] = useState<Array<TApiProvider> | null>(null)
  const [selectedProvider, setSelectedProvider] = useState<TApiProvider | undefined>(undefined);
  const [orders, setOrders] = useState<Array<TApiOrder> | null>(null);
  const [ordersError, setOrdersError] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | undefined>(undefined);
  const [selectedCategory, setSelectedCategory] = useState<OrderCategory | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    Api.Provider.list().then(setProviders)
  }, []);

  useEffect(() => {
    setOrdersError(false);

    Api.Order.listCCPOrders({
      organisation_id: selectedProvider?.id,
      search: searchTerm,
      status: selectedStatus,
      category: selectedCategory,
      limit,
      offset: currentPage * limit
    })
      .then((response => {
        setOrders(response.results);
        setTotalItemsCount(response.count)
      }))
      .catch(() => setOrdersError(true))
  }, [selectedProvider, searchTerm, selectedCategory, selectedStatus, currentPage, limit]);

  const onProviderChange = useCallback((provider: TApiProvider | undefined) => {
    setSelectedProvider(provider);
    setCurrentPage(0);
  }, []);

  const onLimitChange = useCallback((limit) => {
    setLimit(limit);
    setCurrentPage(0);
  }, []);

  const onStatusChange = useCallback((status) => {
    setSelectedStatus(status);
    setCurrentPage(0);
  }, []);

  const onCategoryChange = useCallback((category) => {
    setSelectedCategory(category);
    setCurrentPage(0);
  }, []);

  const onSearchTermChange = useCallback((term) => {
    setSearchTerm(term);
    setCurrentPage(0)
  }, []);

  if (!providers) {
    return <LoadingIndicator />;
  }

  return (
    <div className="ccp-orders-page">
      <Row>
        <Col>
          <PageTitle title="CCP orders" />
        </Col>
      </Row>
      <Row>
        <Col className="controls">
          <TextInput
            className="search"
            value={searchTerm}
            placeholder="Search..."
            onChange={onSearchTermChange}
          />

          <SelectSingleInput
            isSearchable
            isClearable
            itemToOption={provider => ({
              value: provider.id.toString(),
              label: provider.name
            })}
            items={providers}
            name="provider"
            onChange={onProviderChange}
            selectedItem={selectedProvider}
            placeholder="Filter by ccp..."
          />

          <SelectSingleInput
            isClearable
            itemToOption={category => ({
              value: category,
              label: category.toUpperCase()
            })}
            items={SELECTABLE_ORDER_CATEGORIES}
            name="category"
            onChange={onCategoryChange}
            selectedItem={selectedCategory}
            placeholder="Filter by category"
          />

          <SelectSingleInput
            isSearchable
            isClearable
            itemToOption={status => ({
              value: status,
              label: status.toUpperCase()
            })}
            items={SELECTABLE_ORDER_STATUSES}
            name="provider"
            onChange={onStatusChange}
            selectedItem={selectedStatus}
            placeholder="Filter by status"
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <PaginatedOrderList
            orders={orders ?? []}
            loading={!orders}
            error={ordersError}
            total={totalItemsCount}
            perPage={limit}
            currentPage={currentPage}
            onPageChange={page => setCurrentPage(page)}
            onPerPageChange={onLimitChange}
          />
        </Col>
      </Row>
    </div>
  )
}

export default CcpOrders;
